import React, { useState, useEffect } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faFileImport, faPaste, faFileCsv, faKeyboard,
  faArrowRight, faCheckCircle, faExclamationTriangle, faUsers,
  faPlus, faTrash, faInfoCircle, faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { parseTextInput, parseCSV, groupByFamily } from '../utils/directoryParser';
import { batchImportDirectory } from '../services/importService';
import './ImportDirectoryPage.css';

function ImportDirectoryPage() {
  const { currentUser, isAdmin, loadingAuth, userOrgId } = useAuth();
  const navigate = useNavigate();

  // Wizard state
  const [step, setStep] = useState(1);

  // Automation status
  const [inviteAutomationEnabled, setInviteAutomationEnabled] = useState(null);

  // Step 1: Input state
  const [inputTab, setInputTab] = useState('paste');
  const [pasteText, setPasteText] = useState('');
  const [manualRows, setManualRows] = useState([{ firstName: '', lastName: '', email: '' }]);
  const [parseError, setParseError] = useState(null);

  // Step 2: Review state
  const [families, setFamilies] = useState([]);

  // Step 3: Results state
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  // Check if Directory Invite Email automation is enabled
  useEffect(() => {
    if (!userOrgId) return;
    const automationDocId = `${userOrgId}__member_invite`;
    getDoc(doc(db, 'automations', automationDocId)).then((snap) => {
      setInviteAutomationEnabled(snap.exists() && snap.data().enabled === true);
    }).catch(() => {
      setInviteAutomationEnabled(false);
    });
  }, [userOrgId]);

  // --- Auth guards ---
  if (loadingAuth) {
    return (
      <div className="import-page">
        <div className="import-loading">
          <FontAwesomeIcon icon={faSpinner} spin size="2x" />
          <p>Loading...</p>
        </div>
      </div>
    );
  }
  if (!currentUser) return <Navigate to="/login" />;
  if (!isAdmin) {
    return (
      <div className="import-page">
        <div className="import-access-denied">
          <h2>Access Denied</h2>
          <p>Only administrators can import directory data.</p>
          <Link to="/dashboard">Return to Dashboard</Link>
        </div>
      </div>
    );
  }

  // --- Step 1 handlers ---
  const handleParsePaste = () => {
    setParseError(null);
    const entries = parseTextInput(pasteText);
    if (entries.length === 0) {
      setParseError('No entries found. Please enter at least one name per line.');
      return;
    }
    setFamilies(groupByFamily(entries));
    setStep(2);
  };

  const handleCSVUpload = (e) => {
    setParseError(null);
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const entries = parseCSV(event.target.result);
      if (entries.length === 0) {
        setParseError('No entries found in CSV. Check that your file has "name" and "email" columns.');
        return;
      }
      setFamilies(groupByFamily(entries));
      setStep(2);
    };
    reader.readAsText(file);
  };

  const handleManualSubmit = () => {
    setParseError(null);
    const entries = manualRows
      .filter(r => r.firstName.trim() || r.lastName.trim())
      .map(r => ({
        firstName: r.firstName.trim(),
        lastName: r.lastName.trim(),
        email: r.email.trim() || null,
        parseWarning: (!r.firstName.trim() && r.lastName.trim())
          ? 'Single name — assumed as last name' : null,
      }));
    if (entries.length === 0) {
      setParseError('Please enter at least one person.');
      return;
    }
    setFamilies(groupByFamily(entries));
    setStep(2);
  };

  const updateManualRow = (index, field, value) => {
    const updated = [...manualRows];
    updated[index] = { ...updated[index], [field]: value };
    setManualRows(updated);
  };

  const addManualRow = () => {
    setManualRows([...manualRows, { firstName: '', lastName: '', email: '' }]);
  };

  const removeManualRow = (index) => {
    if (manualRows.length <= 1) return;
    setManualRows(manualRows.filter((_, i) => i !== index));
  };

  // --- Step 2 handlers ---
  const updateFamilySurname = (familyIndex, newSurname) => {
    const updated = [...families];
    updated[familyIndex] = { ...updated[familyIndex], surname: newSurname };
    setFamilies(updated);
  };

  const updateMember = (familyIndex, memberIndex, field, value) => {
    const updated = [...families];
    const members = [...updated[familyIndex].members];
    members[memberIndex] = { ...members[memberIndex], [field]: value };
    updated[familyIndex] = { ...updated[familyIndex], members };
    setFamilies(updated);
  };

  const removeMember = (familyIndex, memberIndex) => {
    const updated = [...families];
    const members = updated[familyIndex].members.filter((_, i) => i !== memberIndex);
    if (members.length === 0) {
      setFamilies(updated.filter((_, i) => i !== familyIndex));
    } else {
      updated[familyIndex] = { ...updated[familyIndex], members };
      setFamilies(updated);
    }
  };

  const removeFamily = (familyIndex) => {
    setFamilies(families.filter((_, i) => i !== familyIndex));
  };

  const addMemberToFamily = (familyIndex) => {
    const updated = [...families];
    const members = [...updated[familyIndex].members, { firstName: '', email: null, parseWarning: null }];
    updated[familyIndex] = { ...updated[familyIndex], members };
    setFamilies(updated);
  };

  const addNewFamily = () => {
    setFamilies([...families, {
      surname: '',
      members: [{ firstName: '', email: null, parseWarning: null }],
    }]);
  };

  // --- Validation ---
  const getValidationErrors = () => {
    const errors = [];
    if (families.length === 0) {
      errors.push('No families to import.');
      return errors;
    }
    const allEmails = [];
    families.forEach((family, fi) => {
      if (!family.surname.trim()) {
        errors.push(`Family #${fi + 1} is missing a surname.`);
      }
      family.members.forEach((member, mi) => {
        if (!member.firstName.trim()) {
          errors.push(`${family.surname || 'Family #' + (fi + 1)}: Member #${mi + 1} is missing a first name.`);
        }
        if (member.email) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(member.email)) {
            errors.push(`${family.surname}: "${member.email}" is not a valid email.`);
          }
          const lower = member.email.toLowerCase();
          if (allEmails.includes(lower)) {
            errors.push(`Duplicate email: ${member.email}`);
          }
          allEmails.push(lower);
        }
      });
    });
    return errors;
  };

  // --- Submit ---
  const handleSubmit = async () => {
    const errors = getValidationErrors();
    if (errors.length > 0) {
      setSubmitError(errors.join('\n'));
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      const payload = families.map(f => ({
        surname: f.surname.trim(),
        members: f.members.map(m => ({
          firstName: m.firstName.trim(),
          email: m.email?.trim() || null,
        })),
      }));

      const result = await batchImportDirectory(payload);
      setResults(result);
      setStep(3);
    } catch (err) {
      setSubmitError(err.message || 'Import failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // --- Computed ---
  const totalMembers = families.reduce((sum, f) => sum + f.members.length, 0);
  const membersWithEmail = families.reduce(
    (sum, f) => sum + f.members.filter(m => m.email).length, 0
  );
  const hasWarnings = families.some(f => f.members.some(m => m.parseWarning));

  return (
    <div className="import-page">
      {/* Header */}
      <div className="import-header">
        <div className="container">
          <div className="import-header-content">
            <div className="import-header-left">
              <Link to="/families" className="import-back-btn">
                <FontAwesomeIcon icon={faArrowLeft} />
              </Link>
              <h1 className="import-title">
                <FontAwesomeIcon icon={faFileImport} />
                Import Directory <span className="admin-only-badge">Admin Only</span>
              </h1>
            </div>
            <div className="import-steps">
              <div className={`import-step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
                <span className="step-number">1</span>
                <span className="step-label">Input</span>
              </div>
              <div className="step-connector" />
              <div className={`import-step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
                <span className="step-number">2</span>
                <span className="step-label">Review</span>
              </div>
              <div className="step-connector" />
              <div className={`import-step ${step >= 3 ? 'active' : ''}`}>
                <span className="step-number">3</span>
                <span className="step-label">Results</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="import-content">

          {/* STEP 1: INPUT */}
          {step === 1 && (
            <div className="import-input-step">
              <div className="import-tabs">
                <button
                  className={`import-tab ${inputTab === 'paste' ? 'active' : ''}`}
                  onClick={() => setInputTab('paste')}
                >
                  <FontAwesomeIcon icon={faPaste} />
                  Paste Text
                </button>
                <button
                  className={`import-tab ${inputTab === 'csv' ? 'active' : ''}`}
                  onClick={() => setInputTab('csv')}
                >
                  <FontAwesomeIcon icon={faFileCsv} />
                  Upload CSV
                </button>
                <button
                  className={`import-tab ${inputTab === 'manual' ? 'active' : ''}`}
                  onClick={() => setInputTab('manual')}
                >
                  <FontAwesomeIcon icon={faKeyboard} />
                  Manual Entry
                </button>
              </div>

              {parseError && (
                <div className="import-alert import-alert-danger">
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  {parseError}
                </div>
              )}

              {inputTab === 'paste' && (
                <div className="import-tab-content">
                  <p className="import-hint">
                    Paste one person per line. Supported formats:
                  </p>
                  <div className="import-format-examples">
                    <code>Bob Jones &lt;bob@email.com&gt;</code>
                    <code>Bob Jones, bob@email.com</code>
                    <code>bob@email.com, Bob Jones</code>
                    <code>Bob Jones</code>
                  </div>
                  <textarea
                    className="import-textarea"
                    rows={12}
                    placeholder={'Bob Jones <bob.jones@gmail.com>\nJane Jones <jane.jones@gmail.com>\nTom Smith <tom@email.com>\nAlice Smith'}
                    value={pasteText}
                    onChange={(e) => setPasteText(e.target.value)}
                  />
                  <button
                    className="import-btn import-btn-primary"
                    onClick={handleParsePaste}
                    disabled={!pasteText.trim()}
                  >
                    Parse & Review
                    <FontAwesomeIcon icon={faArrowRight} />
                  </button>
                </div>
              )}

              {inputTab === 'csv' && (
                <div className="import-tab-content">
                  <p className="import-hint">
                    Upload a CSV file with columns for <strong>name</strong> (or <strong>first</strong> / <strong>last</strong>) and <strong>email</strong>.
                  </p>
                  <label className="import-file-upload">
                    <FontAwesomeIcon icon={faFileCsv} size="2x" />
                    <span>Choose CSV File</span>
                    <input
                      type="file"
                      accept=".csv,text/csv"
                      onChange={handleCSVUpload}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>
              )}

              {inputTab === 'manual' && (
                <div className="import-tab-content">
                  <p className="import-hint">
                    Enter names and emails below. People with the same last name will be grouped into families.
                  </p>
                  <div className="import-manual-grid">
                    <div className="import-manual-header">
                      <span>First Name</span>
                      <span>Last Name</span>
                      <span>Email</span>
                      <span></span>
                    </div>
                    {manualRows.map((row, i) => (
                      <div className="import-manual-row" key={i}>
                        <input
                          type="text"
                          placeholder="First name"
                          value={row.firstName}
                          onChange={(e) => updateManualRow(i, 'firstName', e.target.value)}
                        />
                        <input
                          type="text"
                          placeholder="Last name"
                          value={row.lastName}
                          onChange={(e) => updateManualRow(i, 'lastName', e.target.value)}
                        />
                        <input
                          type="email"
                          placeholder="Email (optional)"
                          value={row.email}
                          onChange={(e) => updateManualRow(i, 'email', e.target.value)}
                        />
                        <button
                          className="import-btn-icon"
                          onClick={() => removeManualRow(i)}
                          disabled={manualRows.length <= 1}
                          title="Remove row"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="manual-entry-actions">
                    <button className="import-btn import-btn-secondary" onClick={addManualRow}>
                      <FontAwesomeIcon icon={faPlus} />
                      Add Row
                    </button>
                    <button
                      className="import-btn import-btn-primary"
                      onClick={handleManualSubmit}
                    >
                      Review
                      <FontAwesomeIcon icon={faArrowRight} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* STEP 2: REVIEW */}
          {step === 2 && (
            <div className="import-review-step">
              {inviteAutomationEnabled === true && (
                <div className="import-alert import-alert-info">
                  <FontAwesomeIcon icon={faInfoCircle} />
                  <div>
                    <div>Members with email addresses will receive a welcome email with a link to create their account and update their family's information.</div>
                    <div style={{ marginTop: '0.5rem' }}>Users with notifications enabled for new member additions will be notified for each member imported.</div>
                  </div>
                </div>
              )}
              {inviteAutomationEnabled === false && (
                <div className="import-alert import-alert-info">
                  <FontAwesomeIcon icon={faInfoCircle} />
                  <div>
                    <div><Link to="/communications?tab=automations">Go to the Automations page</Link> to turn on the Directory Invite Email automation to have members with email addresses receive an email invite.</div>
                    <div style={{ marginTop: '0.5rem' }}>Users with notifications enabled for new member additions will be notified for each member imported.</div>
                  </div>
                </div>
              )}

              <div className="import-summary-bar">
                <span>{families.length} {families.length === 1 ? 'family' : 'families'}</span>
                <span className="import-summary-dot" />
                <span>{totalMembers} {totalMembers === 1 ? 'member' : 'members'}</span>
                <span className="import-summary-dot" />
                <span>{membersWithEmail} with email</span>
                {hasWarnings && (
                  <>
                    <span className="import-summary-dot" />
                    <span className="import-summary-warning">
                      <FontAwesomeIcon icon={faExclamationTriangle} /> Needs review
                    </span>
                  </>
                )}
              </div>

              {submitError && (
                <div className="import-alert import-alert-danger">
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{submitError}</pre>
                </div>
              )}

              {families.map((family, fi) => (
                <div className="import-family-card" key={fi}>
                  <div className="import-family-header">
                    <FontAwesomeIcon icon={faUsers} className="import-family-icon" />
                    <input
                      className="import-family-surname"
                      type="text"
                      value={family.surname}
                      onChange={(e) => updateFamilySurname(fi, e.target.value)}
                      placeholder="Family surname"
                    />
                    <span className="import-family-count">
                      {family.members.length} {family.members.length === 1 ? 'member' : 'members'}
                    </span>
                    <button
                      className="import-btn-icon import-btn-danger"
                      onClick={() => removeFamily(fi)}
                      title="Remove family"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                  <div className="import-member-list">
                    {family.members.map((member, mi) => (
                      <div className={`import-member-row ${member.parseWarning ? 'has-warning' : ''}`} key={mi}>
                        <input
                          type="text"
                          className="import-member-name"
                          value={member.firstName}
                          onChange={(e) => updateMember(fi, mi, 'firstName', e.target.value)}
                          placeholder="First name"
                        />
                        <input
                          type="email"
                          className="import-member-email"
                          value={member.email || ''}
                          onChange={(e) => updateMember(fi, mi, 'email', e.target.value || null)}
                          placeholder="Email (optional)"
                        />
                        {member.parseWarning && (
                          <span className="import-member-warning" title={member.parseWarning}>
                            <FontAwesomeIcon icon={faExclamationTriangle} />
                          </span>
                        )}
                        <button
                          className="import-btn-icon"
                          onClick={() => removeMember(fi, mi)}
                          title="Remove member"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    className="import-add-member-btn"
                    onClick={() => addMemberToFamily(fi)}
                  >
                    <FontAwesomeIcon icon={faPlus} />
                    Add Member
                  </button>
                </div>
              ))}

              <div className="import-review-actions">
                <button className="import-btn import-btn-secondary" onClick={addNewFamily}>
                  <FontAwesomeIcon icon={faPlus} />
                  Add Family
                </button>
                <div className="import-review-actions-right">
                  <button
                    className="import-btn import-btn-secondary"
                    onClick={() => setStep(1)}
                  >
                    <FontAwesomeIcon icon={faArrowLeft} />
                    Back
                  </button>
                  <button
                    className="import-btn import-btn-primary"
                    onClick={handleSubmit}
                    disabled={submitting || families.length === 0}
                  >
                    {submitting ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin />
                        Importing...
                      </>
                    ) : (
                      <>
                        Import {totalMembers} {totalMembers === 1 ? 'Member' : 'Members'}
                        <FontAwesomeIcon icon={faArrowRight} />
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* STEP 3: RESULTS */}
          {step === 3 && results && (
            <div className="import-results-step">
              <div className="import-results-card">
                <div className="import-results-icon">
                  <FontAwesomeIcon icon={faCheckCircle} />
                </div>
                <h2>Import Complete</h2>
                <div className="import-results-stats">
                  <div className="import-results-stat">
                    <span className="stat-number">{results.familiesCreated}</span>
                    <span className="stat-label">{results.familiesCreated === 1 ? 'Family' : 'Families'} Created</span>
                  </div>
                  <div className="import-results-stat">
                    <span className="stat-number">{results.membersCreated}</span>
                    <span className="stat-label">{results.membersCreated === 1 ? 'Member' : 'Members'} Created</span>
                  </div>
                  {inviteAutomationEnabled && (
                    <div className="import-results-stat">
                      <span className="stat-number">{membersWithEmail}</span>
                      <span className="stat-label">Welcome Emails Sent</span>
                    </div>
                  )}
                </div>
                <button
                  className="import-btn import-btn-primary"
                  onClick={() => navigate('/families')}
                >
                  View Directory
                  <FontAwesomeIcon icon={faArrowRight} />
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

export default ImportDirectoryPage;
